import compact from 'lodash/compact';
import isNil from 'lodash/isNil';
import upperFirst from 'lodash/upperFirst';
import values from 'lodash/values';
import {
  AddressDTO,
} from 'dtos/addressDTO';
import {
  formatMoney,
  formatNumber,
  formatPercent,
  formatPhone,
  isEmptyString,
} from '@populateui/utils';
import {
  formatDate,
  formatMonth,
} from 'utils/date';

export type UserLike = {
  credentials?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  fullName?: string;
  indexName?: string;
};

export function getAddressString(
  address?: AddressDTO,
  includeCountry = false,
  isShortZipCode = false,
) {
  const {
    street = '',
    unit = '',
    city = '',
    state = '',
    zipCode = '',
    shortZipCode,
  } = address ?? {};

  const displayZip = isShortZipCode ? shortZipCode : zipCode ?? shortZipCode;

  return !isNil(address) && !values(address).every(isEmptyString)
    ? compact([street, unit, city, state, displayZip, includeCountry ? 'USA' : null]).join(' ')
    : '—';
}

const formatTypes = [
  'number',
  'currency',
  'percent',
  'date',
  'phone',
] as const;
export type FormatType = typeof formatTypes[number];

type Formatter = (...params: any[]) => string;

const formatsByType: Readonly<Record<FormatType, Formatter>> = Object.freeze({
  number: formatNumber,
  currency: ({ value }) => formatMoney(value),
  percent: formatPercent,
  date: ({ value }) => formatDate(value),
  phone: ({ value }) => formatPhone(value),
});

export function formatValue(formatType: FormatType, ...params: any[]) {
  return formatsByType[formatType](...params);
}

function formatGenericValue(value: string) {
  return value;
}

type CommonReportParameters = {
  Min?: string;
  Max?: string;
  Date?: string;
  Month?: string;
  Year?: string;
  // add more here;
};

const parameterPrefixes = [
  'from',
  'to',
  'for',
  'for',
  'for',
];

const parameterFormatters = [
  formatDate,
  formatDate,
  formatDate,
  formatMonth,
  formatGenericValue,
];

export function stringifyReportParameters(parameters: Record<string, any> = {}) {
  const {
    Min: from,
    Max: to,
    Date: forDate,
    Month: forMonth,
    Year: forYear,
  } = parameters as CommonReportParameters;

  const result = compact(
    [from, to, forDate, forMonth, forYear]
      .map((value, index) => (
        isNil(value)
          ? null
          : `${parameterPrefixes[index]} ${parameterFormatters[index](value ?? '')}`)),
  );
  return result.length > 0 ? upperFirst(result.join(' ')) : null;
}

export function formatFileSize(size: number): string {
  return size >= 1024 * 1024
    ? `${(size / (1024 * 1024)).toFixed(1)} MB`
    : `${(size / 1024).toFixed(1)} KB`;
}
